import React, { useState, useMemo, useRef, useEffect } from "react";
import { Breadcrumb } from "../../components/ui";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import { useGetTenantsQuery } from "../../store/services/tenants";
import {
  useGetWorkshopsQuery,
  useUpdateWorkshopsSortOrderMutation,
} from "../../store/services/workshops";
import { WorkshopWithMetrics } from "../../models/workshops";
import { getWorkshopStatusLabel, getWorkshopStatusSeverity } from "../../utils/constants/workshops-constants";

export const WorkshopsSortOrder = () => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantOptions = useMemo(
    () =>
      Array.isArray(tenants)
        ? tenants.map((t: any) => ({
            id: String(t.id),
            displayName: `${t.provider || t.id}${t.emailDomain ? ` (${t.emailDomain})` : ""}`.trim(),
          }))
        : [],
    [tenants]
  );

  const {
    data: workshopsData,
    isLoading: isLoadingWorkshops,
    isFetching: isFetchingWorkshops,
    refetch,
  } = useGetWorkshopsQuery(
    selectedTenantId ? { limit: 500, tenantId: selectedTenantId } : ({} as any),
    { skip: !selectedTenantId, refetchOnMountOrArgChange: true }
  );

  const [updateOrder, { isLoading: isUpdating }] = useUpdateWorkshopsSortOrderMutation();

  const [orderedWorkshops, setOrderedWorkshops] = useState<WorkshopWithMetrics[]>([]);

  useEffect(() => {
    if (!workshopsData || !selectedTenantId) {
      setOrderedWorkshops([]);
      return;
    }
    const list = (workshopsData as any)?.data ?? workshopsData;
    if (!Array.isArray(list)) {
      setOrderedWorkshops([]);
      return;
    }
    const sorted = [...list].sort((a: WorkshopWithMetrics, b: WorkshopWithMetrics) => {
      const aZero = (a.sortOrder ?? 0) === 0;
      const bZero = (b.sortOrder ?? 0) === 0;
      if (aZero && !bZero) return 1;
      if (!aZero && bZero) return -1;
      if (!aZero && !bZero) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      return (b.startDate ?? 0) - (a.startDate ?? 0);
    });
    setOrderedWorkshops(sorted);
  }, [workshopsData, selectedTenantId]);

  const dragItem = useRef<number | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    dragItem.current = position;
    if (e.target instanceof HTMLElement) {
      setTimeout(() => {
        if (e.target instanceof HTMLElement) e.target.style.opacity = "0.5";
      }, 0);
    }
  };

  const handleDragEnter = (_e: React.DragEvent<HTMLDivElement>, position: number) => {
    if (dragItem.current === null) return;
    if (dragItem.current !== position) {
      const copy = [...orderedWorkshops];
      const dragged = copy[dragItem.current];
      copy.splice(dragItem.current, 1);
      copy.splice(position, 0, dragged);
      dragItem.current = position;
      setOrderedWorkshops(copy);
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) e.target.style.opacity = "1";
    dragItem.current = null;
  };

  const handleSave = async () => {
    if (!selectedTenantId) return;
    try {
      const payload = orderedWorkshops.map((w, index) => ({
        id: w.id,
        sortOrder: index + 1,
      }));
      await updateOrder({ tenantId: selectedTenantId, workshops: payload }).unwrap();
      toast.success("Orden de talleres actualizado");
      refetch();
    } catch (err) {
      toast.error("Ocurrió un error al actualizar el orden");
    }
  };

  return (
    <>
      <Breadcrumb items={[{ label: "Talleres", to: "/workshops" }, { label: "Ordenar" }]} />
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
        <h1 className="text-center m-0">Editar orden de talleres</h1>
      </div>

      <div className="grid">
        <div className="col-12 md:col-6 mb-4">
          <span className="p-float-label">
            <Dropdown
              className="w-full"
              id="tenantId"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.value)}
              options={tenantOptions}
              optionLabel="displayName"
              optionValue="id"
              loading={isLoadingTenants}
              placeholder="Seleccione un tenant"
            />
            <label htmlFor="tenantId">Tenant</label>
          </span>
          <small className="text-color-secondary block mt-2">
            El orden es independiente por tenant. Los talleres con orden 0 se muestran al final, por fecha de inicio descendente.
          </small>
        </div>

        <div className="col-12">
          {selectedTenantId ? (
            <>
              {isFetchingWorkshops || isLoadingWorkshops ? (
                <div className="flex flex-column justify-content-center align-items-center p-5">
                  <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" />
                  <p className="text-600 mt-3 m-0">Cargando talleres...</p>
                </div>
              ) : orderedWorkshops.length > 0 ? (
                <>
                  <div className="flex justify-content-between mb-2 px-3 text-600 font-semibold w-full">
                    <div style={{ width: "4rem" }} className="text-center">Orden</div>
                    <div className="flex-grow-1 pl-4">Taller</div>
                    <div style={{ width: "10rem" }} className="text-center">Orden Anterior</div>
                  </div>

                  <div className="flex flex-column gap-2 mb-4 w-full">
                    {orderedWorkshops.map((w, index) => {
                      const thumb = w.imageUrls?.[0] || w.imageUrl || "/assets/images/Image-not-found.png";
                      return (
                        <div
                          key={w.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => e.preventDefault()}
                          className="flex align-items-center p-3 border-1 border-300 border-round shadow-1 surface-card cursor-move transition-colors hover:surface-hover"
                        >
                          <div className="flex align-items-center justify-content-center bg-primary text-white font-bold border-circle min-w-min w-3rem h-3rem mr-4 shadow-2 select-none flex-shrink-0">
                            {index + 1}
                          </div>

                          <div
                            className="mr-4 border-round overflow-hidden shadow-1 flex-shrink-0 bg-100"
                            style={{ width: "80px", height: "80px" }}
                          >
                            <img
                              src={thumb}
                              alt={w.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              onError={(e) => (e.currentTarget.src = "/assets/images/Image-not-found.png")}
                            />
                          </div>

                          <div className="flex-grow-1 flex flex-column gap-2 select-none overflow-hidden pr-3">
                            <span className="font-bold text-xl text-800 line-height-2 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                              {w.name}
                            </span>
                            <div className="flex align-items-center flex-wrap gap-4 text-sm text-600 font-medium">
                              <span className="flex align-items-center gap-2">
                                <i className="pi pi-tag text-primary"></i>
                                {w.activityType}
                              </span>
                              {w.startDate && (
                                <span className="flex align-items-center gap-2">
                                  <i className="pi pi-calendar text-primary"></i>
                                  {new Date(w.startDate * 1000).toLocaleDateString("es-AR")}
                                </span>
                              )}
                              <span className="flex align-items-center gap-2">
                                <i className="pi pi-users text-primary"></i>
                                {w.activeEnrollments}/{w.maxCapacity}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge value={getWorkshopStatusLabel(w.status)} severity={getWorkshopStatusSeverity(w.status) as any} />
                              {w.isRolling && <Badge value="Rolling" severity="info" />}
                              {w.buttonType === "CUSTOM_LINK" && <Badge value="Link" severity="warning" />}
                              {w.buttonType === "NONE" && <Badge value="Sin botón" severity="secondary" />}
                            </div>
                          </div>

                          <div className="flex justify-content-center align-items-center flex-shrink-0" style={{ width: "10rem" }}>
                            <div className="text-500 font-semibold w-4rem text-center select-none text-xl">
                              {w.sortOrder || "-"}
                            </div>
                            <div className="text-400 ml-4">
                              <i className="pi pi-bars text-2xl"></i>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-content-end w-full">
                    <Button
                      label="Guardar orden"
                      icon="pi pi-save"
                      onClick={handleSave}
                      loading={isUpdating}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center p-5 border-1 surface-border border-round">
                  <i className="pi pi-inbox text-5xl text-400 mb-3"></i>
                  <p className="text-600 m-0">No hay talleres para este tenant.</p>
                </div>
              )}
            </>
          ) : (
            <div className="p-message p-component p-message-info w-full" role="alert">
              <div className="p-message-wrapper">
                <span className="p-message-icon pi pi-info-circle"></span>
                <div className="p-message-text">Seleccione un tenant para visualizar y ordenar sus talleres.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};
